/**
 * dragWake — keeps the Unity host awake for the length of an EXTERNAL (Files) drag.
 *
 * THE PROBLEM. UIController runs an idle frame-rate governor: 0.6s with no activity and the app
 * drops to 20fps, and on macOS it additionally Thread.Sleeps the MAIN thread ~45ms of every 50ms
 * frame (UIController.SleepThrottledFrameIfMacOS). An external file drag over the embedded WebView
 * produces NO Unity input — AppKit's drag messages are not Unity input, and bare hover deliberately
 * does not count — so 0.6s into a drag the host goes comatose and everything the WebView needs from
 * that main thread starves with it: drag-event delivery, display-refresh relays, compositing.
 * Measured on device during a 58s drag: 13 dragovers/sec reaching the page and 0-2 rAF/sec. The
 * dialog raised by the drop then waits for the user's next real click to wake the app.
 *
 * THE FIX. Every inbound bridge message is now treated as user activity by both WebView hosts
 * (TimelineWebViewPanel / PurchasingWebViewPopupPanel ping UIController.RegisterUserActivity() at
 * their message intercepts). That covers everything the user does inside the web app EXCEPT a
 * file drag, which generates no bridge traffic at all — so this module generates some. The
 * `dragActivity` verb carries no payload and both hosts recognise it as a deliberate no-op: the
 * wake already happened on receipt.
 *
 * Deliberately NOT part of dragDropGovernor.ts. That module is the drag POLICY (accept/refuse) and
 * its contract is bridge-free; this one is host-lifecycle plumbing that only exists embedded.
 *
 * OBSERVATION ONLY: never preventDefault, never write dropEffect, never read items. The governor's
 * dragover already decided this event's fate and dropEffect is last-writer-wins at the end of
 * dispatch, so any touch here would silently overrule it.
 */

import { capabilities } from '@/capabilities.ts';
import { enqueueSendToNative } from '@/bridge/transport';

/** One send per this many ms while the drag is in flight. Must stay clear of the host governor's
 *  0.6s idle window (UIController.IdleThrottleDelaySeconds) with room for a missed tick — a drag
 *  delivers ~13 dragovers/sec, so 300ms costs ~3 tiny messages/sec and leaves 2x headroom. */
const WAKE_INTERVAL_MS = 300;

let lastSentTs = 0;

const sendWake = (): void => {
  lastSentTs = Date.now();
  enqueueSendToNative('dragActivity', {});
};

const wakeOnDragOver = (e: DragEvent): void => {
  if (!e.dataTransfer?.types.includes('Files')) return; // internal reorder / text drags: not ours
  if (Date.now() - lastSentTs < WAKE_INTERVAL_MS) return;
  sendWake();
};

const wakeOnDrop = (e: DragEvent): void => {
  if (!e.dataTransfer?.types.includes('Files')) return;
  // Un-throttled on purpose: this is the message that has the host awake for the dialog the drop
  // is about to raise, and there is no next dragover to carry it.
  sendWake();
};

/** Installs the keepalive on `window` (bubble phase — it observes, so it must not run ahead of the
 *  handlers that act). No-op without the bridge: standalone and the debug harness have no host to
 *  wake, and sends there would be dead weight. Returns an uninstall function — used by the tests;
 *  production never uninstalls. */
export function installDragWakeKeepalive(): () => void {
  if (!capabilities.bridge) return () => {};

  lastSentTs = 0; // a fresh install always wakes on its first drag
  window.addEventListener('dragover', wakeOnDragOver);
  window.addEventListener('drop', wakeOnDrop);
  return () => {
    window.removeEventListener('dragover', wakeOnDragOver);
    window.removeEventListener('drop', wakeOnDrop);
  };
}
